// @ts-nocheck -- vidstack 1.15+ types require TypeScript 5.x; this project uses 4.x.
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MediaPlayer, MediaProvider, Track, isYouTubeProvider, isHLSProvider, } from '@vidstack/react';
// Try to use Vidstack default video layout
import { DefaultVideoLayout, defaultLayoutIcons } from '@vidstack/react/player/layouts/default';
import { useI18n } from '../../../Translation';
// Vidstack styles
import '@vidstack/react/player/styles/base.css';
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
// Error codes from HTML5 MediaError
const MediaErrorCode = {
    ABORT: 1,
    NETWORK: 2,
    DECODE: 3,
    SRC_NOT_SUPPORTED: 4, // Source not supported
};
const isYouTubeUrl = (url) => /youtube\.com|youtu\.be/.test(url);
const isHlsUrl = (url) => /\.m3u8(\?.*)?$/i.test(url);
const mimeFromUrl = (url) => {
    if (/\.mp4(\?.*)?$/i.test(url))
        return 'video/mp4';
    if (/\.webm(\?.*)?$/i.test(url))
        return 'video/webm';
    if (isHlsUrl(url))
        return 'application/x-mpegURL';
    return undefined;
};
// Convert a "mm:ss" chapter timestamp to WebVTT "HH:MM:SS.sss".
const toWebVttTimestamp = (mmss) => {
    const parts = mmss.split(':').map((n) => Number(n) || 0);
    const totalSeconds = parts.length === 3
        ? parts[0] * 3600 + parts[1] * 60 + parts[2]
        : parts[0] * 60 + (parts[1] ?? 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.000`;
};
// Build a WebVTT chapters file from the project's VideoChapter[] shape and return a Blob URL.
// Vidstack's DefaultVideoLayout picks the chapters track up automatically and renders the
// running-figure chapter menu icon plus the timeline markers.
const chaptersToVttBlobUrl = (chapters) => {
    if (!chapters || chapters.length === 0) {
        return null;
    }
    const lines = ['WEBVTT', ''];
    chapters.forEach((chapter, idx) => {
        const start = toWebVttTimestamp(chapter.time);
        const next = chapters[idx + 1];
        const end = next ? toWebVttTimestamp(next.time) : '99:59:59.999';
        lines.push(`${start} --> ${end}`, chapter.title, '');
    });
    return URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/vtt' }));
};
const VidStackPlayer = ({ playerConfig, sourceConfig, onStart, onProgress, onEnded, disableSeek, width, height, initialTime = 0, isLoadingPosition = false, onSourceDimensions, }) => {
    const i18n = useI18n();
    const player = useRef(null);
    const [hasStarted, setHasStarted] = useState(false);
    const [duration, setDuration] = useState(0);
    const [lastTime, setLastTime] = useState(0);
    const [hasSetInitialTime, setHasSetInitialTime] = useState(false);
    // Error handling state
    const [fatalError, setFatalError] = useState(null);
    const [retryKey, setRetryKey] = useState(0);
    const retryCountRef = useRef(0);
    const retryTimeoutRef = useRef(null);
    const resumeTimeRef = useRef(0);
    const maxRetries = 3;
    const baseRetryDelay = 2000; // 2 seconds, with exponential backoff
    const url = playerConfig?.url || '';
    const autoPlay = !!playerConfig?.playing;
    const muted = !!playerConfig?.muted;
    const showControls = playerConfig?.controls !== false;
    // YouTube must always be driven through the vidstack layout, never through YouTube's own
    // controls: the provider force-pauses any playback it did not initiate itself, so every
    // play/seek via the native YouTube UI (or an ad resume) kills playback
    // (https://github.com/vidstack/player/issues/1514).
    const useNativeControls = showControls && !isYouTubeUrl(url);
    const poster = typeof playerConfig?.light === 'string' ? playerConfig.light : undefined;
    const dataAttrs = useMemo(() => {
        const out = {};
        if (playerConfig) {
            Object.keys(playerConfig).forEach((k) => {
                if (k.startsWith('data-'))
                    out[k] = playerConfig[k];
            });
        }
        return out;
    }, [playerConfig]);
    const chapters = Array.isArray(playerConfig?.chapters) ? playerConfig.chapters : undefined;
    const [chaptersVttUrl, setChaptersVttUrl] = useState(null);
    const [fitMode, setFitMode] = useState(() => {
        if (typeof window === 'undefined')
            return 'auto';
        const stored = window.localStorage.getItem('videoPlayerFitMode');
        return stored === 'contain' || stored === 'cover' || stored === 'auto' ? stored : 'auto';
    });
    const [sourceAspectRatio, setSourceAspectRatio] = useState(null);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        window.localStorage.setItem('videoPlayerFitMode', fitMode);
    }, [fitMode]);
    const cycleFitMode = useCallback(() => {
        setFitMode((prev) => prev === 'auto' ? 'contain' : prev === 'contain' ? 'cover' : 'auto');
    }, []);
    const fitModeLabel = fitMode === 'auto' ? 'Auto' : fitMode === 'contain' ? 'Anpassen' : 'Füllen';
    const effectiveAspectRatio = fitMode === 'auto' && sourceAspectRatio ? sourceAspectRatio : '16 / 9';
    const effectiveObjectFit = fitMode === 'cover' ? 'cover' : 'contain';
    useEffect(() => {
        const url = chaptersToVttBlobUrl(chapters);
        setChaptersVttUrl(url);
        return () => {
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [chapters]);
    const wantsHls = !!sourceConfig?.file?.forceHLS || isHlsUrl(url) || url.startsWith('rtmp://');
    // Build src for Vidstack
    const src = useMemo(() => {
        if (!url)
            return undefined;
        if (isYouTubeUrl(url)) {
            return url; // Vidstack can infer YouTube provider from URL
        }
        if (wantsHls) {
            return { src: url, type: 'application/x-mpegURL' };
        }
        const type = mimeFromUrl(url);
        return type ? { src: url, type } : url;
    }, [url, wantsHls]);
    const streamType = useMemo(() => {
        // Treat HLS or rtmp as live if "/live" or explicit HLS and not file extension known
        if (wantsHls && /live|broadcast/i.test(url))
            return 'live';
        return 'on-demand';
    }, [url, wantsHls]);
    useEffect(() => {
        if (!hasSetInitialTime && !isLoadingPosition && initialTime > 0 && player.current && duration > 0) {
            player.current.currentTime = initialTime;
            setLastTime(initialTime);
            setHasSetInitialTime(true);
        }
    }, [initialTime, isLoadingPosition, hasSetInitialTime, duration]);
    const computeProgress = useCallback((currentTime) => {
        const dur = duration || player.current?.duration || 0;
        const played = dur > 0 ? currentTime / dur : 0;
        return {
            played,
            playedSeconds: currentTime,
            loaded: 0,
            loadedSeconds: 0,
        };
    }, [duration]);
    // Moved to useEffect below — onProviderChange/onProviderSetup are no longer JSX props in vidstack 1.15+
    const handleTimeUpdate = () => {
        const ct = player.current?.currentTime ?? 0;
        if (ct > lastTime)
            setLastTime(ct);
        onProgress(computeProgress(ct));
    };
    const handleLoadedMetadata = () => {
        const dur = player.current?.duration ?? 0;
        if (dur)
            setDuration(dur);
        // Capture the intrinsic aspect ratio so the 'auto' fit mode can mirror the source dimensions.
        const playerEl = player.current?.el ?? null;
        // getElementsByTagName avoids querySelector's overloaded-return-type deep-instantiation in TS4
        const videoEl = playerEl ? (playerEl.getElementsByTagName('video')[0] ?? null) : null;
        if (videoEl && videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
            setSourceAspectRatio(`${videoEl.videoWidth} / ${videoEl.videoHeight}`);
            onSourceDimensions?.(videoEl.videoWidth, videoEl.videoHeight);
        }
        // Resume from retry position if we have one
        if (resumeTimeRef.current > 0 && player.current) {
            player.current.currentTime = resumeTimeRef.current;
            setLastTime(resumeTimeRef.current);
            resumeTimeRef.current = 0; // Reset after using
        }
        else if (!hasSetInitialTime && !isLoadingPosition && initialTime > 0 && player.current) {
            player.current.currentTime = initialTime;
            setLastTime(initialTime);
            setHasSetInitialTime(true);
        }
    };
    const handlePlay = () => {
        if (!hasStarted) {
            setHasStarted(true);
            onStart();
        }
    };
    const handleEnd = () => {
        onEnded();
        try {
            if (typeof (playerConfig?.onEnded) === 'function') {
                playerConfig.onEnded();
            }
        }
        catch (e) {
            // ignore
        }
    };
    const handleSeeking = () => {
        if (!disableSeek)
            return;
        const ct = player.current?.currentTime ?? 0;
        // Prevent any seek away from the last known time.
        if (Math.abs(ct - lastTime) > 0.5) {
            if (player.current)
                player.current.currentTime = lastTime;
        }
    };
    const handleKeyDown = (e) => {
        if (!disableSeek)
            return;
        const codes = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
        if (codes.includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    // Disable timeline interactions if seeking is locked
    const layoutClass = disableSeek ? 'pointer-events-auto [&_.plyr__progress]:pointer-events-none' : '';
    // Cleanup retry timeout on unmount
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, []);
    // Reset retry state when URL changes
    useEffect(() => {
        retryCountRef.current = 0;
        resumeTimeRef.current = 0;
        setRetryKey(0);
        setFatalError(null);
    }, [url]);
    const attemptRetry = useCallback(() => {
        if (retryCountRef.current >= maxRetries) {
            setFatalError(i18n['player.error.loadFailed']);
            return;
        }
        retryCountRef.current += 1;
        const delay = baseRetryDelay * Math.pow(2, retryCountRef.current - 1); // Exponential backoff
        console.log(`[VidStackPlayer] Attempting retry ${retryCountRef.current}/${maxRetries} in ${delay}ms`);
        // Store current time before reload
        if (player.current) {
            resumeTimeRef.current = player.current.currentTime || 0;
        }
        retryTimeoutRef.current = setTimeout(() => {
            // Increment retryKey to force MediaPlayer remount
            setRetryKey(prev => prev + 1);
        }, delay);
    }, [i18n]);
    const handleError = useCallback((detail, nativeEvent) => {
        const errorCode = detail?.code;
        const errorMessage = detail?.message || 'Unknown error';
        console.warn('[VidStackPlayer] Error event:', { code: errorCode, message: errorMessage, detail });
        // Determine if error is recoverable
        switch (errorCode) {
            case MediaErrorCode.ABORT:
                // Abort errors are usually recoverable (user action, network hiccup)
                console.log('[VidStackPlayer] Abort error - attempting recovery');
                attemptRetry();
                break;
            case MediaErrorCode.NETWORK:
                // Network errors - attempt retry with backoff
                console.log('[VidStackPlayer] Network error - attempting recovery');
                attemptRetry();
                break;
            case MediaErrorCode.DECODE:
                // Decode errors are usually fatal
                console.error('[VidStackPlayer] Decode error - fatal');
                setFatalError(i18n['player.error.decodeFailed']);
                break;
            case MediaErrorCode.SRC_NOT_SUPPORTED:
                // Source not supported is fatal
                console.error('[VidStackPlayer] Source not supported - fatal');
                setFatalError(i18n['player.error.formatNotSupported']);
                break;
            default:
                // Unknown errors - attempt retry first
                if (retryCountRef.current < maxRetries) {
                    console.log('[VidStackPlayer] Unknown error - attempting recovery');
                    attemptRetry();
                }
                else {
                    setFatalError(i18n['player.error.unknown']);
                }
                break;
        }
    }, [attemptRetry, i18n]);
    const handleHlsError = useCallback((detail) => {
        // HLS.js specific errors
        console.warn('[VidStackPlayer] HLS error:', detail);
        const errorType = detail?.type;
        const errorDetails = detail?.details;
        const fatal = detail?.fatal;
        if (!fatal) {
            // Non-fatal HLS errors - HLS.js handles recovery automatically
            console.log('[VidStackPlayer] Non-fatal HLS error, letting HLS.js handle recovery');
            return;
        }
        // Fatal HLS errors - attempt manual recovery
        if (errorType === 'networkError' || errorDetails?.includes('frag')) {
            // Fragment loading or network errors - attempt retry
            console.log('[VidStackPlayer] Fatal HLS network/fragment error - attempting recovery');
            attemptRetry();
        }
        else if (errorType === 'mediaError') {
            // Media errors in HLS - try recovery
            console.log('[VidStackPlayer] Fatal HLS media error - attempting recovery');
            attemptRetry();
        }
        else {
            // Other fatal errors
            if (retryCountRef.current < maxRetries) {
                attemptRetry();
            }
            else {
                setFatalError(i18n['player.error.streamFailed']);
            }
        }
    }, [attemptRetry, i18n]);
    // Clear error state when playback resumes successfully
    const handleCanPlay = useCallback(() => {
        // Playback recovered successfully
        if (retryCountRef.current > 0) {
            console.log('[VidStackPlayer] Playback recovered after retry');
        }
        // Clear any pending retry timeout
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, []);
    // onProviderChange / onProviderSetup / error moved from JSX props to DOM events (vidstack 1.15+)
    useEffect(() => {
        const playerEl = player.current;
        if (!playerEl)
            return;
        const onProviderChange = (e) => {
            const provider = e.detail;
            if (provider && isYouTubeProvider(provider)) {
                provider.cookies = true;
            }
        };
        const onProviderSetup = (e) => {
            const provider = e.detail;
            if (provider && isHLSProvider(provider)) {
                provider.instance?.on('hlsError', (_event, data) => {
                    handleHlsError(data);
                });
            }
        };
        const onError = (e) => {
            const detail = e.detail;
            handleError(detail, e);
        };
        playerEl.addEventListener('provider-change', onProviderChange);
        playerEl.addEventListener('provider-setup', onProviderSetup);
        playerEl.addEventListener('error', onError);
        return () => {
            playerEl.removeEventListener('provider-change', onProviderChange);
            playerEl.removeEventListener('provider-setup', onProviderSetup);
            playerEl.removeEventListener('error', onError);
        };
    }, [handleHlsError, handleError]);
    // Show fatal error overlay
    if (fatalError) {
        return (<div className="w-full aspect-video bg-slate-900 text-white font-sans overflow-hidden rounded-md flex items-center justify-center" style={{ width: width || '100%', height: height || 'auto' }}>
                <div className="text-center p-6">
                    <svg className="w-12 h-12 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <p className="text-lg">{fatalError}</p>
                    <button className="mt-4 px-4 py-2 bg-brand-secondary hover:bg-brand-secondary-dark rounded text-white" onClick={() => {
                setFatalError(null);
                retryCountRef.current = 0;
            }}>
                        {i18n['player.error.retry']}
                    </button>
                </div>
            </div>);
    }
    return (<div className="relative w-full" style={{ width: width || '100%', height: height || 'auto' }} {...dataAttrs}>
            <MediaPlayer key={`player-${url}-${retryKey}`} className="w-full h-full bg-slate-900 text-white font-sans overflow-hidden rounded-md" title={playerConfig?.chapters?.title || 'Video'} aspectRatio={effectiveAspectRatio} style={{ '--media-object-fit': effectiveObjectFit }} src={src} poster={poster} viewType="video" streamType={streamType} playsInline controls={useNativeControls} muted={muted} autoPlay={autoPlay} ref={player} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onPlay={handlePlay} onEnded={handleEnd} onSeeking={handleSeeking} onKeyDown={handleKeyDown} onCanPlay={handleCanPlay}>
                <MediaProvider iframeProps={{ width: '100%', height: '100%' }} style={{ width: '100%', height: '100%' }}/>
                {chaptersVttUrl !== null ? (<Track src={chaptersVttUrl} kind="chapters" default/>) : null}
                {showControls ? <DefaultVideoLayout className={layoutClass} icons={defaultLayoutIcons}/> : null}
            </MediaPlayer>
            {showControls ? (<button type="button" onClick={cycleFitMode} title={'Bildanpassung: ' + fitModeLabel} aria-label={'Bildanpassung wechseln. Aktuell: ' + fitModeLabel} className="absolute top-3 right-3 z-20 px-2 py-1 rounded-md bg-black/55 hover:bg-black/75 text-white text-xs font-medium tracking-wide select-none transition-colors">
                    {fitModeLabel}
                </button>) : null}
        </div>);
};
VidStackPlayer.displayName = 'VidStackPlayer';
export default VidStackPlayer;
